import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import { Form, Formik } from "formik";
import { atom, useAtom } from "jotai";
import { useState } from "react";
import { useMutation } from "react-query";
import * as Yup from "yup";
import { TablePopUp, TopDisplay } from "../../../../components";
import {
  modalAtom,
  notificationAtom,
  openAtom,
  openNAtom,
} from "../../../../context";
import apiCall from "../../../../utils/apiCall";
import { TopLink } from "../";

type ExamApplicationSubject = {
  ExamApplicationSubjectId: number;
  SubjectName: string;
  SubjectCode: string;
  Grade: string;
  ExamFee: number;
  IsDeferred: boolean;
};

type ExamApplication = {
  ExamApplicationId: number;
  ExamPeriodName: string;
  LevelName: string;
  ExamPeriodId: number;
  LevelId: number;
  StudentNo: string;
  TotalFee: number;
  CreateDate: string;
  Subjects: ExamApplicationSubject[];
};

const HeadStyle = {
  borderBottom: "1px solid var(--dark-green-color)",
  padding: "15px 5px",
};

const DataStyle = {
  padding: "5px 5px",
};

const ROWSTYLE = styled.tr`
  & > td:last-of-type > * {
    display: none;
  }
  :hover > td {
    background-color: var(--table-highlight);
    &:last-of-type > * {
      display: block;
    }
  }
  &.active > td:last-of-type > * {
    display: block !important;
  }
`;

const DeleteExamApplicationAtom = atom<number | null>(null);

export default function DeleteExamApplication() {
  const [, set_modal] = useAtom(modalAtom);
  const [, set_open] = useAtom(openAtom);
  const [active] = useAtom(DeleteExamApplicationAtom);
  const [applications, setApplications] = useState<ExamApplication[]>([]);
  const [studentNumber, setStudentNumber] = useState("");

  const handleCheckApplication = () => {
    set_modal({
      header: "Check Exam Applications",
      content: (
        <CheckExamApplication
          onSuccess={(data, studentNo) => {
            setApplications(data);
            setStudentNumber(studentNo);
          }}
        />
      ),
    });
    set_open(true);
  };

  const handleDeleteSuccess = (deletedApplication: ExamApplication) => {
    setApplications((prev) =>
      prev.filter(
        (item) =>
          item.ExamApplicationId !== deletedApplication.ExamApplicationId
      )
    );
  };

  return (
    <Box>
      <TopLink />
      <TopDisplay
        label="Delete Exam Application"
        button_array={[
          <Button
            onClick={handleCheckApplication}
            bg="var(--dark-green-color)"
            color="var(--white-color)"
            type="button"
          >
            Check Exam Applications
          </Button>,
        ]}
      />

      <Flex mt="25px" flexDir="column" gap="10px">
        <Text fontSize="sm" mt="10px">
          {studentNumber
            ? `Showing ${applications.length} exam application(s) for ${studentNumber}`
            : "Click the button above to check a student's exam applications"}
        </Text>
      </Flex>

      {applications.length > 0 ? (
        <TableContainer mt="20px">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th sx={HeadStyle}>Application Id</Th>
                <Th sx={HeadStyle}>Student No</Th>
                <Th sx={HeadStyle}>Exam Period</Th>
                <Th sx={HeadStyle}>Level</Th>
                <Th sx={HeadStyle}>Total Fee</Th>
                <Th sx={HeadStyle}>Subjects</Th>
                <Th sx={HeadStyle}>Create Date</Th>
                <Th sx={HeadStyle} w="50px"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {applications.map((application) => (
                <ROWSTYLE
                  key={application.ExamApplicationId}
                  className={
                    application.ExamApplicationId === active ? "active" : ""
                  }
                >
                  <Td sx={DataStyle}>{application.ExamApplicationId}</Td>
                  <Td sx={DataStyle}>{application.StudentNo}</Td>
                  <Td sx={DataStyle}>{application.ExamPeriodName}</Td>
                  <Td sx={DataStyle}>{application.LevelName}</Td>
                  <Td sx={DataStyle}>{application.TotalFee}</Td>
                  <Td sx={DataStyle}>
                    {application.Subjects?.length || 0}
                  </Td>
                  <Td sx={DataStyle}>
                    {application.CreateDate
                      ? new Date(application.CreateDate).toLocaleDateString()
                      : "N/A"}
                  </Td>
                  <Td sx={DataStyle} w="50px" h="50px">
                    <DeleteExamApplicationPopUp
                      data={application}
                      onDeleteSuccess={handleDeleteSuccess}
                    />
                  </Td>
                </ROWSTYLE>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      ) : (
        <Text mt="20px">No Exam Application Found</Text>
      )}
    </Box>
  );
}

const CheckExamApplicationSchema = Yup.object({
  studentNumber: Yup.string().required("Student number is required"),
});

const CheckExamApplication = (props: {
  onSuccess: (data: ExamApplication[], studentNumber: string) => void;
}) => {
  const [, set_open] = useAtom(openAtom);
  const [, set_Nopen] = useAtom(openNAtom);
  const [, set_notification] = useAtom(notificationAtom);

  const { mutate: checkApplications, isLoading } = useMutation({
    mutationKey: ["check-exam-applications-for-delete"],
    mutationFn: async (studentNumber: string) =>
      apiCall().get(
        `/Exam/examApplication/byStudentNoForDelete?studentNo=${encodeURIComponent(
          studentNumber
        )}`
      ),
    onSuccess(response: any, studentNo: string) {
      const data = Array.isArray(response?.data?.Data) ? response.data.Data : [];

      props.onSuccess(data, studentNo);
      set_notification({
        type: "success",
        message: response?.data?.Message || "Exam applications fetched",
      });
      set_Nopen(true);
      set_open(false);
    },
    onError(error: any) {
      set_notification({
        type: "error",
        message:
          error?.response?.data?.Message ||
          error?.data?.Message ||
          "Error occurred",
      });
      set_Nopen(true);
    },
  });

  return (
    <Formik
      initialValues={{ studentNumber: "" }}
      validationSchema={CheckExamApplicationSchema}
      onSubmit={(values) => {
        checkApplications(values.studentNumber.trim());
      }}
    >
      {({ errors, touched, values, handleChange }) => (
        <Form>
          <Flex flexDir="column" gap="20px" minW="400px">
            <Box>
              <label>Student Number</label>
              <Input
                name="studentNumber"
                value={values.studentNumber}
                onChange={handleChange}
                placeholder="Enter student number"
                borderColor="green"
              />
              {errors.studentNumber && touched.studentNumber && (
                <Text color="red.500" fontSize="sm">
                  {errors.studentNumber}
                </Text>
              )}
            </Box>

            <Button
              type="submit"
              isDisabled={isLoading}
              bg="var(--dark-green-color)"
              color="white"
            >
              {isLoading ? "Loading..." : "Submit"}
            </Button>
          </Flex>
        </Form>
      )}
    </Formik>
  );
};

const DeleteExamApplicationPopUp = (props: {
  data: ExamApplication;
  onDeleteSuccess: (data: ExamApplication) => void;
}) => {
  const { data, onDeleteSuccess } = props;
  const [active, set_active] = useAtom(DeleteExamApplicationAtom);
  const [, set_open] = useAtom(openAtom);
  const [, set_modal] = useAtom(modalAtom);
  const [, set_Nopen] = useAtom(openNAtom);
  const [, set_notification] = useAtom(notificationAtom);

  const { mutate: deleteApplication, isLoading } = useMutation({
    mutationKey: ["delete-exam-application"],
    mutationFn: async () =>
      apiCall().post("/Exam/examApplication/delete", {
        StudentNo: data.StudentNo,
        ExamPeriodId: data.ExamPeriodId,
      }),
    onSuccess(response: any) {
      onDeleteSuccess(data);
      set_notification({
        type: "success",
        message:
          response?.data?.Message || "Exam application deleted successfully",
      });
      set_Nopen(true);
    },
    onError(error: any) {
      set_notification({
        type: "error",
        message:
          error?.response?.data?.Message ||
          error?.data?.Message ||
          "Error occurred",
      });
      set_Nopen(true);
    },
    onSettled() {
      set_open(false);
      set_active(null);
    },
  });

  const handleClick = () => {
    if (data.ExamApplicationId === active) {
      set_active(null);
      return;
    }

    set_active(data.ExamApplicationId);
  };

  const handleDelete = () => {
    set_modal({
      header: "Delete Exam Application",
      content: (
        <Grid gap="20px">
          <GridItem>
            Are you sure you want to delete this exam application?
          </GridItem>
          <GridItem as={Flex} justify="space-between" p="20px 0" gap="20px">
            <Button
              isDisabled={isLoading}
              onClick={() => deleteApplication()}
              bg="var(--dark-green-color)"
              color="var(--white-color)"
            >
              {isLoading ? "Loading" : "Delete"}
            </Button>
            <Button
              isDisabled={isLoading}
              variant="outline"
              bg="var(--white-color)"
              onClick={() => set_open(false)}
              border="1px solid var(--dark-green-color)"
              color="var(--dark-green-color)"
            >
              Cancel
            </Button>
          </GridItem>
        </Grid>
      ),
    });
    set_open(true);
  };

  return (
    <TablePopUp
      click={handleClick}
      options={[{ label: "Delete", click: handleDelete }]}
    />
  );
};
